import { useEffect, useState } from "react";
import { api, apiError, formatINR, downloadCsv } from "@/lib/api";
import { Button } from "@/components/ui/button";
import CollectionTrend from "@/components/CollectionTrend";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, IndianRupee, CheckCircle2, Download, FileText } from "lucide-react";

const PERIODS = [{ v: "day", label: "Day-wise", type: "date" }, { v: "month", label: "Month-wise", type: "month" }, { v: "year", label: "Year-wise", type: "number" }];
const defaultFor = (p) => { const d = new Date(); const iso = d.toISOString(); return p === "day" ? iso.slice(0, 10) : p === "month" ? iso.slice(0, 7) : iso.slice(0, 4); };
const periodLabel = (period, key) => {
  if (period === "year") return key;
  if (period === "month") return new Date(`${key}-01`).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  return new Date(key).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
};

export default function DoctorReports({ doctors, role }) {
  const [period, setPeriod] = useState("month");
  const [date, setDate] = useState(defaultFor("month"));
  const [doctorId, setDoctorId] = useState("all");
  const [data, setData] = useState(null);
  const isDoctor = role === "doctor";

  useEffect(() => {
    if (!date) return undefined;
    let stale = false;
    const params = { period, date };
    if (!isDoctor && doctorId !== "all") params.doctor_id = doctorId;
    api.get("/reports/doctors", { params }).then((r) => { if (!stale) setData(r.data); }).catch((e) => toast.error(apiError(e)));
    return () => { stale = true; };
  }, [period, date, doctorId, isDoctor]);

  const changePeriod = (p) => { setPeriod(p); setDate(defaultFor(p)); };
  const inputType = PERIODS.find((p) => p.v === period).type;
  const max = data ? Math.max(1, ...data.rows.map((r) => r.collection)) : 1;

  return (
    <div className="space-y-6" data-testid="doctor-reports">
      <div className="flex flex-wrap items-end gap-3 bg-white rounded-2xl border border-slate-200 p-4">
        <div className="space-y-1">
          <div className="text-xs text-slate-500">Period</div>
          <Select value={period} onValueChange={changePeriod}>
            <SelectTrigger className="w-36" data-testid="report-period-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PERIODS.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <div className="text-xs text-slate-500">{period === "year" ? "Year" : period === "month" ? "Month" : "Date"}</div>
          <Input type={inputType} value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="report-date-input"
            {...(period === "year" ? { min: 2000, max: 2100 } : {})} />
        </div>
        {!isDoctor && (
          <div className="space-y-1">
            <div className="text-xs text-slate-500">Doctor</div>
            <Select value={doctorId} onValueChange={setDoctorId}>
              <SelectTrigger className="w-56" data-testid="report-doctor-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All doctors</SelectItem>
                {doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <Button variant="outline" className="ml-auto" data-testid="export-report-button"
          onClick={() => downloadCsv("/reports/doctors/export", { period, date, ...(!isDoctor && doctorId !== "all" ? { doctor_id: doctorId } : {}) }, "doctor-report.csv").catch((e) => toast.error(apiError(e)))}>
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
        {role === "admin" && period === "month" && (
          <Button className="bg-[#0F172A] hover:bg-slate-800 text-white" data-testid="monthly-statement-button"
            onClick={() => downloadCsv("/reports/monthly-statement", { month: date, ...(doctorId !== "all" ? { doctor_id: doctorId } : {}) }, `statement-${date}.pdf`).then(() => toast.success("Statement downloaded")).catch((e) => toast.error(apiError(e)))}>
            <FileText className="h-4 w-4 mr-2" /> Statement PDF
          </Button>
        )}
      </div>

      <CollectionTrend />

      {data && (
        <>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-[#0F172A] text-white rounded-2xl p-6">
              <div className="text-teal-300 text-xs uppercase tracking-wider">Total collection · {periodLabel(period, data.key)}</div>
              <div className="font-head text-4xl font-extrabold mt-2 font-mono" data-testid="report-total-collection">{formatINR(data.total_collection)}</div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="text-slate-400 text-xs uppercase tracking-wider">Patients seen</div>
              <div className="font-head text-4xl font-extrabold mt-2 text-slate-900" data-testid="report-total-patients">{data.total_patients}</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {data.rows.length === 0 ? <div className="p-12 text-center text-slate-400">No visits in this period.</div> : (
              <div className="divide-y" data-testid="report-rows">
                {data.rows.map((r) => (
                  <div key={r.doctor_id} className="p-5" data-testid="report-row">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div className="font-semibold text-slate-900">{r.doctor_name}</div>
                      <div className="flex items-center gap-5 text-sm text-slate-600">
                        <span className="flex items-center gap-1"><Users className="h-4 w-4 text-teal-600" /> {r.patients} patients</span>
                        <span className="flex items-center gap-1"><CheckCircle2 className="h-4 w-4 text-blue-600" /> {r.completed} completed</span>
                        <span className="flex items-center gap-1 font-mono font-semibold text-teal-700"><IndianRupee className="h-4 w-4" /> {formatINR(r.collection)}</span>
                      </div>
                    </div>
                    <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${(r.collection / max) * 100}%` }} /></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
